import { makeAutoObservable } from 'mobx';
import { orderBy } from 'lodash';

import { RootStore } from './RootStore';

import {
  AlbumRepository,
  AssetRepository,
  StoryRepository,
} from '@src/repositories';
import { Logger, LogManager } from '@src/utility/Logger';

import { Artifact, Showcase, Story } from '@src/types.ts/story';

export enum BRollType {
  story = 'story',
  organization = 'organization',
}

export type BRollAsset = {
  id: string;
  type: 'photo' | 'video';
  status: 'pending' | 'uploading' | 'uploaded' | 'failed';
  description: string;
  url: string;
  createdAt: string;
  file?: File;
  errorUploading?: boolean;
};

export class BRollStore {
  private _rootStore: RootStore;
  private _logger: Logger;
  private _isLoading: boolean = false;
  private _bRollType: BRollType | null = null;
  private _organization: Showcase | null = null;
  private _story: Story | null = null;
  private _assetsToUpload: BRollAsset[] = [];
  private _contributor: Record<'name' | 'email', string> | null = null;

  get albumRepository(): AlbumRepository {
    if (!this._rootStore.datoClientStore) {
      throw new Error('Album repository not initialized');
    }
    return this._rootStore.datoClientStore.albumRepository;
  }

  get storyRepository(): StoryRepository {
    if (!this._rootStore.datoClientStore) {
      throw new Error('Story repository not initialized');
    }
    return this._rootStore.datoClientStore.storyRepository;
  }

  get assetRepository(): AssetRepository {
    if (!this._rootStore.datoClientStore) {
      throw new Error('Asset repository not initialized');
    }
    return this._rootStore.datoClientStore.assetRepository;
  }

  get userIdentityStore() {
    return this._rootStore.userIdentityStore;
  }

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;
    this._logger = LogManager.getLogger('b-roll-store');
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async initialize(params: { storyId?: string; organizationId?: string }) {
    this._logger.info('Initializing BRollStore...');

    this.setIsLoading(true);
    try {
      if (params.storyId) {
        await this.initializeWithStory(params.storyId);
      } else if (params.organizationId) {
        await this.initializeWithOrganization(params.organizationId);
      }
    } catch (err) {
      this._logger.error('Error initializing BRollStore', err);
    } finally {
      this.setIsLoading(false);
    }
  }

  private async initializeWithStory(storyId: string) {
    this._logger.info('Initializing BRollStore with story', storyId);
    const story = await this.storyRepository.findOne(storyId);
    if (!story) {
      this._logger.error('Story not found', storyId);
      return;
    }
    this._logger.info('Story found', story);
    this.setStory(story);
    this.setOrganization(story.primaryShowcase);
    this.setBRollType(BRollType.story);
  }

  private async initializeWithOrganization(organizationId: string) {
    this._logger.info(
      'Initializing BRollStore with organization',
      organizationId,
    );
    const organization = await this.albumRepository.findOne(organizationId);
    if (!organization) {
      this._logger.error('Organization not found', organizationId);
      return;
    }
    this._logger.info('Organization found', organization);
    this.setOrganization(organization);
    this.setBRollType(BRollType.organization);
  }

  private marArtifactsToAssets(artifacts: Artifact[]): BRollAsset[] {
    return artifacts.map((artifact) => ({
      id: artifact.id,
      type: artifact.responsiveImage ? 'photo' : 'video',
      status: 'uploaded',
      description: artifact.title,
      url: artifact.url!,
      createdAt: artifact._createdAt,
    }));
  }

  addPendingAsset(asset: BRollAsset) {
    this._assetsToUpload.push(asset);
  }

  removePendingAsset(id: string) {
    this._assetsToUpload = this._assetsToUpload.filter((a) => a.id !== id);
  }

  appendArtifact(artifact: Artifact) {
    if (this.isStory && this.story) {
      if (artifact.responsiveImage) {
        this.story.storyArtifacts?.push(artifact);
      } else {
        this.story.storyArtifactsVideo?.push(artifact);
      }
    } else if (this.isOrganization && this.organization) {
      if (artifact.responsiveImage) {
        this.organization.organizationArtifacts?.push(artifact);
      } else {
        this.organization.organizationArtifactsVideo?.push(artifact);
      }
    }
  }

  setAssetStatus(id: string, status: BRollAsset['status']) {
    const asset = this.bRollAssets.find((a) => a.id === id);
    if (!asset) return;
    asset.status = status;
  }

  // Computed properties
  get bRollAssets(): BRollAsset[] {
    const assets: BRollAsset[] = [...this._assetsToUpload];
    if (this.isStory && this.story) {
      assets.push(
        ...this.marArtifactsToAssets(this.story.storyArtifacts || []),
        ...this.marArtifactsToAssets(this.story.storyArtifactsVideo || []),
      );
    }
    if (this.isOrganization && this.organization) {
      assets.push(
        ...this.marArtifactsToAssets(
          this.organization.organizationArtifacts || [],
        ),
        ...this.marArtifactsToAssets(
          this.organization.organizationArtifactsVideo || [],
        ),
      );
    }
    return orderBy(assets, 'createdAt', 'desc');
  }
  get hasPendingAssets() {
    return this.bRollAssets.some((a) => a.status === 'pending');
  }
  get hasUploadingAssets() {
    return this.bRollAssets.some((a) => a.status === 'uploading');
  }

  // Getters
  get logger() {
    return this._logger;
  }
  get isLoading() {
    return this._isLoading;
  }
  get isStory() {
    return this._bRollType === BRollType.story;
  }
  get isOrganization() {
    return this._bRollType === BRollType.organization;
  }
  get story() {
    return this._story;
  }
  get organization() {
    return this._organization;
  }
  get contributor() {
    return this._contributor;
  }

  // Setters
  setIsLoading(isLoading: boolean) {
    this._isLoading = isLoading;
  }
  setBRollType(bRollType: BRollType | null) {
    this._bRollType = bRollType;
  }
  setOrganization(organization: Showcase | null) {
    this._organization = organization;
  }
  setStory(story: Story | null) {
    this._story = story;
  }
  setStoryArtifacts(artifacts?: Artifact[]) {
    if (!this._story) return;
    this._story.storyArtifacts = artifacts;
  }
  setStoryArtifactsVideo(artifacts?: Artifact[]) {
    if (!this._story) return;
    this._story.storyArtifactsVideo = artifacts;
  }
  setOrganizationArtifacts(artifacts?: Artifact[]) {
    if (!this._organization) return;
    this._organization.organizationArtifacts = artifacts;
  }
  setOrganizationArtifactsVideo(artifacts?: Artifact[]) {
    if (!this._organization) return;
    this._organization.organizationArtifactsVideo = artifacts;
  }
  setContributor(contributor: Record<'name' | 'email', string> | null) {
    this._contributor = contributor;
  }
}
